import { Outlet, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import Sidebar from '../components/Sidebar';
import { workspaceApi } from '../services/workspace.service';
import { useWorkspaceStore } from '../stores/workspace.store';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { SidebarProvider, SidebarInset } from '../components/ui/sidebar';
import { Toaster } from '../components/ui/toaster';
import { useThemePreset } from '../hooks/use-theme-preset';
import { CtrlTicker } from '../components/CtrlTicker';
import { CtrlParticles } from '../components/CtrlParticles';

export default function DashboardLayout() {
  const location = useLocation();
  const { setWorkspaces, setCurrentWorkspace, currentWorkspace } = useWorkspaceStore();
  const [workspaceName, setWorkspaceName] = useState('');
  const queryClient = useQueryClient();
  const themePreset = useThemePreset();
  const isCtrl = themePreset === 'ctrl';

  // Detectar se está no builder (modo de edição)
  const isBuilderMode = location.pathname.includes('/edit') || location.pathname.includes('/new');

  const { data: workspaces, isLoading, isError, error } = useQuery({
    queryKey: ['workspaces'],
    queryFn: workspaceApi.getAll,
  });

  const createWorkspaceMutation = useMutation({
    mutationFn: workspaceApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      setWorkspaceName('');
    },
  });

  useEffect(() => {
    if (workspaces && workspaces.length > 0) {
      setWorkspaces(workspaces);
      // Se não há workspace atual ou o atual não pertence à lista carregada
      // (pode acontecer após troca de conta), seleciona o primeiro
      const stillValid = currentWorkspace && workspaces.some((w: any) => w.id === currentWorkspace.id);
      if (!stillValid) {
        setCurrentWorkspace(workspaces[0]);
      }
    }
  }, [workspaces, currentWorkspace, setWorkspaces, setCurrentWorkspace]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando workspace...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md">
          <div className="text-destructive text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold mb-2">
            Erro ao carregar workspaces
          </h2>
          <p className="text-muted-foreground mb-4">
            {error instanceof Error ? error.message : 'Não foi possível conectar ao servidor'}
          </p>
          <Button
            onClick={() => window.location.reload()}
          >
            Tentar Novamente
          </Button>
        </div>
      </div>
    );
  }

  if (!workspaces || workspaces.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="max-w-md p-8">
          <div className="text-center">
            <div className="text-muted-foreground text-6xl mb-4">📁</div>
            <h2 className="text-xl font-semibold mb-2">
              Nenhum workspace encontrado
            </h2>
            <p className="text-muted-foreground mb-6">
              Crie seu primeiro workspace para começar
            </p>
            
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (workspaceName.trim()) {
                  createWorkspaceMutation.mutate({ name: workspaceName });
                }
              }}
              className="space-y-4"
            >
              <Input
                type="text"
                placeholder="Nome do workspace (ex: Meu Projeto)"
                value={workspaceName}
                onChange={(e) => setWorkspaceName(e.target.value)}
                disabled={createWorkspaceMutation.isPending}
              />
              <Button
                type="submit"
                disabled={createWorkspaceMutation.isPending || !workspaceName.trim()}
                className="w-full"
              >
                {createWorkspaceMutation.isPending ? 'Criando...' : 'Criar Workspace'}
              </Button>
              
              {createWorkspaceMutation.isError && (
                <p className="text-destructive text-sm">
                  Erro ao criar workspace. Tente novamente.
                </p>
              )}
            </form>
          </div>
        </Card>
      </div>
    );
  }

  if (!currentWorkspace) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando workspace...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {isCtrl && !isBuilderMode && <CtrlTicker />}
      {isCtrl && !isBuilderMode && <CtrlParticles />}
      <SidebarProvider>
        {!isBuilderMode && <Sidebar />}
        <SidebarInset>
          <main className={`flex-1 ${!isBuilderMode ? 'p-6' : ''}`}>
            <Outlet />
          </main>
        </SidebarInset>
        <Toaster />
      </SidebarProvider>
    </>
  );
}
